import React from 'react'

import PropTypes from 'prop-types'

import AuthService from '@shared/services/AuthService'

const AuthContext = React.createContext({})

function useAuth () {
  const context = React.useContext(AuthContext)

  if (!context) {
    throw new Error('useContext must be used within an AuthProvider')
  }

  return context
}

const AuthProvider = ({ children }) => {
  const authService = React.useMemo(() => new AuthService(), [])

  const [authData, setAuthData] = React.useState(() => {
    const { token, refresh, module, user, unit } = authService.getAuthData()

    if (token && user) {
      return {
        token,
        refresh,
        user,
        unit,
        module
      }
    }

    return {}
  })

  React.useEffect(
    function sync () {
      const syncBrowserTabsState = () => {
        console.info('Auth State Synced')
        setAuthData(authService.getAuthData())
      }

      window.addEventListener('storage', syncBrowserTabsState)

      authService.syncUserData().then(syncBrowserTabsState)

      return () => {
        window.removeEventListener('storage', syncBrowserTabsState)
      }
    },
    [authService]
  )

  const signIn = React.useCallback(async ({ email: login, password }) => {
    const { token, refresh, user, unit } = await authService.signIn({
      login,
      password
    })

    setAuthData({ token, refresh, user, unit })
  }, [authService])

  const authByToken = React.useCallback(async ({ authorization, unit_id, module_id }) => {
    const {
      token,
      refresh,
      user,
      unit,
      module
    } = await authService.authByToken({
      authorization,
      unit_id,
      module_id
    })

    setAuthData({ token, refresh, user, unit, module })
  }, [authService])

  const updateUser = React.useCallback((user) => {
    authService.setUser(user)
    setAuthData((data) => ({ ...data, user }))
  }, [authService])

  const updateUnit = React.useCallback((unit) => {
    authService.setUnit(unit)
    setAuthData((data) => ({ ...data, unit }))
  }, [authService])

  const updateModule = React.useCallback((module) => {
    authService.setModule(module)
    setAuthData((data) => ({ ...data, module }))
  }, [authService])

  const signOut = React.useCallback(() => {
    authService.signOut()
    setAuthData({})
  }, [authService])

  return (
    <AuthContext.Provider
      value={{
        user: authData.user,
        unit: authData.unit,
        module: authData.module,
        signIn,
        signOut,
        authByToken,
        updateUser,
        updateUnit,
        updateModule
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

AuthProvider.propTypes = {
  children: PropTypes.any
}

export { useAuth, AuthProvider }
